import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';
import Loader from '../../components/common/Loader';
import UserHeader from '../../components/common/UserHeader';
import banner from '../../assets/headerBanner.png';
import { FaHeart, FaComment, FaShare, FaCalendar, FaClock } from 'react-icons/fa';
import Container from '../../assets/Container.png';
import SEO from '../common/SEO'; 

const BlogDetails = () => {
  const { slug, id } = useParams();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userName, setUserName] = useState('');
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [liked, setLiked] = useState(false);
  const [showCommentBox, setShowCommentBox] = useState(false);

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
      easing: 'ease-out-cubic'
    });
  }, []);

  useEffect(() => {
    fetchBlogDetails();
    const likedKey = `liked_${slug || id}`;
    setLiked(localStorage.getItem(likedKey) === 'true');
  }, [slug, id]);

  const fetchBlogDetails = async () => {
    try {
      setIsLoading(true);
      let response;
      
      if (slug) {
        try {
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}/blog-post/get-blog-by-slug/${slug}`
          );
        } catch (slugError) {
          console.log('Slug not found, trying with ID...');
          response = await axios.get(
            `${process.env.REACT_APP_API_URL}/blog-post/get-blog-by-id/${slug}`
          );
        }
      } else if (id) {
        response = await axios.get(
          `${process.env.REACT_APP_API_URL}/blog-post/get-blog-by-id/${id}`
        );
      } else {
        throw new Error('No blog identifier provided');
      }
      
      setBlog(response.data);
      setComments(response.data.comments || []);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching blog details:', error);
      setError('Failed to load blog details. The blog may have been removed.');
      setIsLoading(false);
    }
  };

  const handleLike = async () => {
    if (!blog) return;
    const likedKey = `liked_${blog._id}`;
    if (localStorage.getItem(likedKey)) {
      alert('You already liked this post!');
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog-post/${blog._id}/like`
      );
      setBlog({ ...blog, likes: res.data.likes });
      localStorage.setItem(likedKey, 'true');
      setLiked(true);
    } catch (err) {
      console.error('Error liking blog:', err);
    }
  };

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    
    if (!userName.trim()) {
      alert('Please enter your name.');
      return;
    }
    
    if (!comment.trim()) {
      alert('Please enter a comment.');
      return;
    }

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/blog-post/${blog._id}/comment`,
        {
          name: userName,
          text: comment,
        }
      );
      setComments(res.data);
      setComment('');
      setUserName('');
      setShowCommentBox(false);
    } catch (err) {
      console.error('Error posting comment:', err);
      alert('Failed to post comment. Please try again.');
    }
  };

  const handleShare = async () => {
    if (!blog) return;
    const shareUrl = `${window.location.origin}/blog/${blog.slug || blog._id}`;
    const shareData = {
      title: blog?.metaTitle || blog?.title || 'Awesome Blog',
      text: blog?.metaDescription || 'Check out this amazing blog!',
      url: shareUrl,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        await axios.post(
          `${process.env.REACT_APP_API_URL}/blog-post/${blog._id}/share`
        );
        setBlog({ ...blog, shares: (blog.shares || 0) + 1 });
      } else {
        navigator.clipboard.writeText(shareUrl);
        alert('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Error sharing blog:', err);
    }
  };

  if (isLoading) {
    return (
      <>
        <UserHeader headerImg={banner} pageName="Blog Details" mainText="Home" />
        <div className="py-10 px-4 md:px-10">
          <div className="rounded-md flex justify-center items-center border border-slate-400 p-4">
            <Loader /> <span className="ml-2 font-semibold">Please Wait...</span>
          </div>
        </div>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <UserHeader headerImg={banner} pageName="Blog Details" mainText="Home" />
        <div className="py-10 px-4 md:px-10 text-red-600 font-semibold">
          {error || 'Blog not found.'}
        </div>
      </>
    );
  }

  return (
    <React.Fragment>
      {/* SEO Meta Tags using the reusable SEO component */}
      <SEO 
        title={blog.metaTitle || blog.title}
        description={blog.metaDescription || blog.title}
        keywords={blog.keywords}
        canonical={blog.canonical || `${window.location.origin}/blog/${blog.slug || blog._id}`}
        image={blog.image?.[0]}
        type="article"
        publishedTime={blog.dateOfPublish}
        author={blog.author}
      />

      <UserHeader headerImg={banner} pageName="Blog Details" mainText="Home" />

      <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-4 md:py-10 px-4 md:px-10">
        <div className="mx-auto">
          {/* Back Button */}
          <div data-aos="fade-right" className="mb-6">
            <Link
              to="/blog-list"
              className="inline-flex items-center text-[#2F9BD6] hover:text-[#1C1C25] font-semibold transition-colors duration-300"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Blog List
            </Link>
          </div>

          {/* SEO-Friendly URL Display */}
          {/* <div className="mb-4 text-sm text-gray-500 bg-white/50 px-4 py-2 rounded-lg inline-block">
            <span className="font-medium">🔗 URL:</span> /blog/{blog.slug || blog._id}
          </div> */}

          {/* Main Card */}
          <article className="overflow-hidden">
            {/* Hero Image */}
            <div 
              data-aos="zoom-in" 
              className="relative h-[500px] overflow-hidden group"
            >
              <img
                src={blog.image?.[0] || Container}
                alt={blog.title}
                className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
            </div>

            {/* Engagement Bar */}
            <div 
              data-aos="fade-up" 
              data-aos-delay="200"
              className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-gray-200 px-8 py-4"
            >
              <div className="flex items-center justify-around max-w-md mx-auto">
                <button
                  onClick={handleLike}
                  className={`flex flex-col items-center gap-1 transition-all duration-300 transform hover:scale-110 ${
                    liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
                  }`}
                >
                  <FaHeart className={`text-3xl ${liked ? 'animate-pulse' : ''}`} />
                  <span className="text-sm font-semibold">{blog.likes || 0}</span>
                </button>

                <a 
                  href="#comments" 
                  className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-500 transition-all duration-300 transform hover:scale-110"
                >
                  <FaComment className="text-3xl" />
                  <span className="text-sm font-semibold">{comments.length}</span>
                </a>

                <button
                  onClick={handleShare}
                  className="flex flex-col items-center gap-1 text-gray-600 hover:text-green-500 transition-all duration-300 transform hover:scale-110"
                >
                  <FaShare className="text-3xl" />
                  <span className="text-sm font-semibold">{blog.shares || 0}</span>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="">
              {/* Title */}
              <h1 
                data-aos="fade-up" 
                data-aos-delay="300"
                className="text-2xl md:text-4xl font-extrabold text-[#1C1C25] mb-6 leading-tight"
              >
                {blog.title}
              </h1>

              {/* Meta Info */}
              <div 
                data-aos="fade-up" 
                data-aos-delay="400"
                className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-gray-200"
              >
                <div className="flex items-center gap-2 text-gray-600">
                  <FaCalendar className="text-[#2F9BD6]" />
                  <span className="text-sm font-medium">
                    {new Date(blog.dateOfPublish).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <FaClock className="text-[#2F9BD6]" />
                  <span className="text-sm font-medium">By {blog.author}</span>
                </div>
              </div>

              {/* Blog Content */}
              <div
                data-aos="fade-up"
                data-aos-delay="500"
                className="prose prose-lg max-w-none text-gray-700 leading-relaxed text-justify"
                dangerouslySetInnerHTML={{ 
                  __html: Array.isArray(blog.description) 
                    ? blog.description.join('') 
                    : blog.description 
                }}
                style={{
                  fontSize: '1.125rem',
                  lineHeight: '1.8'
                }}
              />

              {/* Comments Section */}
              <div 
                id="comments" 
                data-aos="fade-up" 
                data-aos-delay="600"
                className="mt-16 pt-10 border-t-2 border-gray-200"
              >
                <h2 className="text-3xl font-bold mb-8 text-[#1C1C25] flex items-center gap-3">
                  <FaComment className="text-[#2F9BD6]" />
                  Comments ({comments.length})
                </h2>

                {/* Add Comment Button or Form */}
                {!showCommentBox ? (
                  <div className="mb-10" data-aos="zoom-in">
                    <button
                      onClick={() => setShowCommentBox(true)}
                      className="w-full bg-gradient-to-r from-[#2F9BD6] to-[#1C8AC7] text-white px-8 py-4 rounded-2xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
                    >
                      <FaComment className="text-xl" />
                      Add a Comment
                    </button>
                  </div>
                ) : (
                  <form 
                    onSubmit={handleCommentSubmit} 
                    className="mb-10 bg-gradient-to-br from-blue-50 to-gray-50 p-6 rounded-2xl"
                    data-aos="zoom-in"
                  >
                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#1C1C25] mb-2">
                        Your Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        className="w-full border-2 border-gray-300 rounded-xl p-4 focus:outline-none focus:border-[#2F9BD6] focus:ring-2 focus:ring-[#2F9BD6]/20 transition-all duration-300"
                        placeholder="Enter your name..."
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                        required
                      />
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-semibold text-[#1C1C25] mb-2">
                        Your Comment <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        className="w-full border-2 border-gray-300 rounded-xl p-4 focus:outline-none focus:border-[#2F9BD6] focus:ring-2 focus:ring-[#2F9BD6]/20 transition-all duration-300 resize-none"
                        rows="4"
                        placeholder="Share your thoughts..."
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        required
                      />
                    </div>

                    <div className="flex gap-3">
                      <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-[#2F9BD6] to-[#1C8AC7] text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                      >
                        Post Comment
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowCommentBox(false);
                          setUserName('');
                          setComment('');
                        }}
                        className="px-6 py-3 rounded-full font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-100 transition-all duration-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                )}

                {/* Comments List */}
                <div className="space-y-6">
                  {comments.length > 0 ? (
                    comments.map((c, i) => (
                      <div
                        key={c._id || i}
                        data-aos="fade-up"
                        data-aos-delay={i * 100}
                        className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
                      >
                        <div className="flex items-center gap-3 mb-3">
                          <div>
                            <p className="font-bold text-[#1C1C25] text-lg">{c.name}</p>
                          </div>
                        </div>
                        <p className="text-gray-700 leading-relaxed">{c.text}</p>
                      </div>
                    ))
                  ) : (
                    <div 
                      className="text-center py-12 bg-gray-50 rounded-xl"
                      data-aos="fade-up"
                    >
                      <FaComment className="text-5xl text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No comments yet. Be the first to share your thoughts!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>
        </div>
      </div>
    </React.Fragment>
  );
};

export default BlogDetails;


// import React, { useEffect, useState } from 'react';
// import { useParams, Link } from 'react-router-dom';
// import axios from 'axios';
// import AOS from 'aos';
// import 'aos/dist/aos.css';
// import Loader from '../../components/common/Loader';
// import UserHeader from '../../components/common/UserHeader';
// import banner from '../../assets/headerBanner.png';
// import { FaHeart, FaComment, FaShare, FaCalendar, FaClock } from 'react-icons/fa';
// import Container from '../../assets/Container.png';

// const BlogDetails = () => {
//   const { id } = useParams();
//   const [blog, setBlog] = useState(null);
//   const [isLoading, setIsLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [userName, setUserName] = useState('');
//   const [comment, setComment] = useState('');
//   const [comments, setComments] = useState([]);
//   const [liked, setLiked] = useState(false);
//   const [showCommentBox, setShowCommentBox] = useState(false);

//   useEffect(() => {
//     AOS.init({
//       duration: 1000,
//       once: true,
//       easing: 'ease-out-cubic'
//     });
//   }, []);

//   useEffect(() => {
//     fetchBlogDetails();
//     const likedKey = `liked_${id}`;
//     setLiked(localStorage.getItem(likedKey) === 'true');
//   }, [id]);

//   const fetchBlogDetails = async () => {
//     try {
//       setIsLoading(true);
//       const res = await axios.get(`${process.env.REACT_APP_API_URL}/blog-post/get-blog-by-id/${id}`);
//       setBlog(res.data);
//       setComments(res.data.comments || []);
//       setIsLoading(false);
//     } catch (error) {
//       console.error('Error fetching blog details:', error);
//       setError('Failed to load blog details.');
//       setIsLoading(false);
//     }
//   };

//   const handleLike = async () => {
//     const likedKey = `liked_${id}`;
//     if (localStorage.getItem(likedKey)) {
//       alert('You already liked this post!');
//       return;
//     }

//     try {
//       const res = await axios.post(`${process.env.REACT_APP_API_URL}/blog-post/${id}/like`);
//       setBlog({ ...blog, likes: res.data.likes });
//       localStorage.setItem(likedKey, 'true');
//       setLiked(true);
//     } catch (err) {
//       console.error('Error liking blog:', err);
//     }
//   };

//   const handleCommentClick = () => {
//     setShowCommentBox(true);
//   };

//   const handleCommentSubmit = async (e) => {
//     e.preventDefault();
    
//     if (!userName.trim()) {
//       alert('Please enter your name.');
//       return;
//     }
    
//     if (!comment.trim()) {
//       alert('Please enter a comment.');
//       return;
//     }

//     try {
//       const res = await axios.post(`${process.env.REACT_APP_API_URL}/blog-post/${id}/comment`, {
//         name: userName,
//         text: comment,
//       });
//       setComments(res.data);
//       setComment('');
//       setUserName('');
//       setShowCommentBox(false);
//     } catch (err) {
//       console.error('Error posting comment:', err);
//       alert('Failed to post comment. Please try again.');
//     }
//   };

//   const handleShare = async () => {
//     const shareData = {
//       title: blog?.title || 'Awesome Blog',
//       text: 'Check out this amazing blog!',
//       url: window.location.href,
//     };

//     try {
//       if (navigator.share) {
//         await navigator.share(shareData);
//         await axios.post(`${process.env.REACT_APP_API_URL}/blog-post/${id}/share`);
//         setBlog({ ...blog, shares: (blog.shares || 0) + 1 });
//       } else {
//         navigator.clipboard.writeText(window.location.href);
//         alert('Link copied to clipboard!');
//       }
//     } catch (err) {
//       console.error('Error sharing blog:', err);
//     }
//   };

//   return (
//     <React.Fragment>
//       <UserHeader headerImg={banner} pageName="Blog Details" mainText="Home" />

//       {isLoading ? (
//         <div className="py-10 px-4 md:px-10">
//           <div className="rounded-md flex justify-center items-center border border-slate-400 p-4">
//             <Loader /> <span className="ml-2 font-semibold">Please Wait...</span>
//           </div>
//         </div>
//       ) : error ? (
//         <div className="py-10 px-4 md:px-10 text-red-600 font-semibold">{error}</div>
//       ) : !blog ? (
//         <div className="py-10 px-4 md:px-10 text-gray-700 font-semibold">Blog not found.</div>
//       ) : (
//         <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-4 md:py-10 px-4 md:px-10">
//           <div className="mx-auto">
//             {/* Back Button */}
//             <div data-aos="fade-right" className="mb-6">
//               <Link
//                 to="/blog-list"
//                 className="inline-flex items-center text-[#2F9BD6] hover:text-[#1C1C25] font-semibold transition-colors duration-300"
//               >
//                 <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
//                 </svg>
//                 Back to Blog List
//               </Link>
//             </div>

//             {/* Main Card */}
//             <article className="overflow-hidden">
//               {/* Hero Image */}
//               <div 
//                 data-aos="zoom-in" 
//                 className="relative h-[500px] overflow-hidden group"
//               >
//                 <img
//                   src={blog.image?.[0] || Container}
//                   alt={blog.title}
//                   className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
//                 />
//                 <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
//               </div>

//               {/* Engagement Bar */}
//               <div 
//                 data-aos="fade-up" 
//                 data-aos-delay="200"
//                 className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-gray-200 px-8 py-4"
//               >
//                 <div className="flex items-center justify-around max-w-md mx-auto">
//                   <button
//                     onClick={handleLike}
//                     className={`flex flex-col items-center gap-1 transition-all duration-300 transform hover:scale-110 ${
//                       liked ? 'text-red-500' : 'text-gray-600 hover:text-red-500'
//                     }`}
//                   >
//                     <FaHeart className={`text-3xl ${liked ? 'animate-pulse' : ''}`} />
//                     <span className="text-sm font-semibold">{blog.likes || 0}</span>
//                   </button>

//                   <a 
//                     href="#comments" 
//                     className="flex flex-col items-center gap-1 text-gray-600 hover:text-blue-500 transition-all duration-300 transform hover:scale-110"
//                   >
//                     <FaComment className="text-3xl" />
//                     <span className="text-sm font-semibold">{comments.length}</span>
//                   </a>

//                   <button
//                     onClick={handleShare}
//                     className="flex flex-col items-center gap-1 text-gray-600 hover:text-green-500 transition-all duration-300 transform hover:scale-110"
//                   >
//                     <FaShare className="text-3xl" />
//                     <span className="text-sm font-semibold">{blog.shares || 0}</span>
//                   </button>
//                 </div>
//               </div>

//               {/* Content */}
//               <div className="">
//                 {/* Title */}
//                 <h1 
//                   data-aos="fade-up" 
//                   data-aos-delay="300"
//                   className="text-2xl md:text-4xl font-extrabold text-[#1C1C25] mb-6 leading-tight"
//                 >
//                   {blog.title}
//                 </h1>

//                 {/* Meta Info */}
//                 <div 
//                   data-aos="fade-up" 
//                   data-aos-delay="400"
//                   className="flex flex-wrap gap-6 mb-8 pb-8 border-b border-gray-200"
//                 >
//                   <div className="flex items-center gap-2 text-gray-600">
//                     <FaCalendar className="text-[#2F9BD6]" />
//                     <span className="text-sm font-medium">{new Date().toLocaleDateString()}</span>
//                   </div>
//                   <div className="flex items-center gap-2 text-gray-600">
//                     <FaClock className="text-[#2F9BD6]" />
//                     <span className="text-sm font-medium">5 min read</span>
//                   </div>
//                 </div>

//                 {/* Blog Content */}
//                 <div
//                   data-aos="fade-up"
//                   data-aos-delay="500"
//                   className="prose prose-lg max-w-none text-gray-700 leading-relaxed text-justify"
//                   dangerouslySetInnerHTML={{ __html: blog.description }}
//                   style={{
//                     fontSize: '1.125rem',
//                     lineHeight: '1.8'
//                   }}
//                 />

//                 {/* Comments Section */}
//                 <div 
//                   id="comments" 
//                   data-aos="fade-up" 
//                   data-aos-delay="600"
//                   className="mt-16 pt-10 border-t-2 border-gray-200"
//                 >
//                   <h2 className="text-3xl font-bold mb-8 text-[#1C1C25] flex items-center gap-3">
//                     <FaComment className="text-[#2F9BD6]" />
//                     Comments ({comments.length})
//                   </h2>

//                   {/* Add Comment Button or Form */}
//                   {!showCommentBox ? (
//                     <div className="mb-10" data-aos="zoom-in">
//                       <button
//                         onClick={handleCommentClick}
//                         className="w-full bg-gradient-to-r from-[#2F9BD6] to-[#1C8AC7] text-white px-8 py-4 rounded-2xl font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3"
//                       >
//                         <FaComment className="text-xl" />
//                         Add a Comment
//                       </button>
//                     </div>
//                   ) : (
//                     <form 
//                       onSubmit={handleCommentSubmit} 
//                       className="mb-10 bg-gradient-to-br from-blue-50 to-gray-50 p-6 rounded-2xl"
//                       data-aos="zoom-in"
//                     >
//                       {/* Name Input */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-semibold text-[#1C1C25] mb-2">
//                           Your Name <span className="text-red-500">*</span>
//                         </label>
//                         <input
//                           type="text"
//                           className="w-full border-2 border-gray-300 rounded-xl p-4 focus:outline-none focus:border-[#2F9BD6] focus:ring-2 focus:ring-[#2F9BD6]/20 transition-all duration-300"
//                           placeholder="Enter your name..."
//                           value={userName}
//                           onChange={(e) => setUserName(e.target.value)}
//                           required
//                         />
//                       </div>

//                       {/* Comment Textarea */}
//                       <div className="mb-4">
//                         <label className="block text-sm font-semibold text-[#1C1C25] mb-2">
//                           Your Comment <span className="text-red-500">*</span>
//                         </label>
//                         <textarea
//                           className="w-full border-2 border-gray-300 rounded-xl p-4 focus:outline-none focus:border-[#2F9BD6] focus:ring-2 focus:ring-[#2F9BD6]/20 transition-all duration-300 resize-none"
//                           rows="4"
//                           placeholder="Share your thoughts..."
//                           value={comment}
//                           onChange={(e) => setComment(e.target.value)}
//                           required
//                         />
//                       </div>

//                       {/* Buttons */}
//                       <div className="flex gap-3">
//                         <button
//                           type="submit"
//                           className="flex-1 bg-gradient-to-r from-[#2F9BD6] to-[#1C8AC7] text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
//                         >
//                           Post Comment
//                         </button>
//                         <button
//                           type="button"
//                           onClick={() => {
//                             setShowCommentBox(false);
//                             setUserName('');
//                             setComment('');
//                           }}
//                           className="px-6 py-3 rounded-full font-semibold border-2 border-gray-300 text-gray-700 hover:bg-gray-100 transition-all duration-300"
//                         >
//                           Cancel
//                         </button>
//                       </div>
//                     </form>
//                   )}

//                   {/* Comments List */}
//                   <div className="space-y-6">
//                     {comments.length > 0 ? (
//                       comments.map((c, i) => (
//                         <div
//                           key={i}
//                           data-aos="fade-up"
//                           data-aos-delay={i * 100}
//                           className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow duration-300"
//                         >
//                           <div className="flex items-center gap-3 mb-3">
//                             {/* <div className="w-12 h-12 bg-gradient-to-br from-[#2F9BD6] to-[#1C8AC7] rounded-full flex items-center justify-center text-white font-bold text-lg">
//                               {c.name.charAt(0).toUpperCase()}
//                             </div> */}
//                             <div>
//                               <p className="font-bold text-[#1C1C25] text-lg">{c.name}</p>
//                               {/* <p className="text-xs text-gray-500">Just now</p> */}
//                             </div>
//                           </div>
//                           <p className="text-gray-700 leading-relaxed pl-15">{c.text}</p>
//                         </div>
//                       ))
//                     ) : (
//                       <div 
//                         className="text-center py-12 bg-gray-50 rounded-xl"
//                         data-aos="fade-up"
//                       >
//                         <FaComment className="text-5xl text-gray-300 mx-auto mb-4" />
//                         <p className="text-gray-500 text-lg">No comments yet. Be the first to share your thoughts!</p>
//                       </div>
//                     )}
//                   </div>
//                 </div>
//               </div>
//             </article>
//           </div>
//         </div>
//       )}
//     </React.Fragment>
//   );
// };

// export default BlogDetails;


